import React from 'react';
import { useTranslation } from 'react-i18next';
import type { LandingSection, LandingFeatureItem, LandingStepItem } from '../types';
import { Card, CardContent } from '../../../components/ui/card';
import {
    AnimatedZapIcon,
    AnimatedShieldIcon,
    AnimatedGlobeIcon,
    AnimatedSparklesIcon,
    AnimatedLayersIcon,
    AnimatedHeadphonesIcon,
} from '../../../components/icons/AnimatedFeatureIcons';

interface LandingSectionsProps {
    sections: LandingSection[];
}

const iconMap: Record<string, React.FC<{ className?: string }>> = {
    zap: AnimatedZapIcon,
    shield: AnimatedShieldIcon,
    globe: AnimatedGlobeIcon,
    sparkles: AnimatedSparklesIcon,
    layers: AnimatedLayersIcon,
    headphones: AnimatedHeadphonesIcon,
};

/**
 * Feature grid section.
 */
const FeatureGrid: React.FC<{ section: LandingSection }> = ({ section }) => {
    const items = section.items as LandingFeatureItem[];

    return (
        <section className="py-20 bg-gradient-to-b from-slate-950 to-slate-900">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Section header */}
                <div className="text-center mb-16">
                    <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
                        {section.title}
                    </h2>
                    <p className="text-lg text-slate-400 max-w-2xl mx-auto">
                        {section.subtitle}
                    </p>
                </div>

                {/* Feature cards grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {items.map((item, index) => {
                        const Icon = iconMap[item.icon] || AnimatedZapIcon;
                        return (
                            <div
                                key={index}
                                className="hover:-translate-y-2 transition-transform"
                            >
                                <Card
                                    className="bg-slate-800/50 border-slate-700/50 hover:border-violet-500/50 transition-all duration-300 hover:shadow-xl hover:shadow-violet-500/10 group h-full"
                                >
                                    <CardContent className="p-6">
                                        <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-xl bg-gradient-to-br from-violet-500 to-indigo-500 flex items-center justify-center mb-6 relative overflow-hidden group-hover:scale-110 group-hover:rotate-3 transition-transform shadow-lg shadow-violet-500/20 group-hover:shadow-violet-500/40">
                                            {/* Glow effect */}
                                            <div className="absolute inset-0 bg-gradient-to-br from-violet-400/30 to-indigo-400/30 rounded-xl blur-md group-hover:blur-lg transition-all" />
                                            {/* Shimmer effect */}
                                            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer-sweep" />
                                            <div className="relative z-10">
                                                <Icon className="w-10 h-10 sm:w-12 sm:h-12 text-white drop-shadow-2xl filter brightness-110" />
                                            </div>
                                        </div>
                                        <h3 className="text-xl font-semibold text-white mb-2 group-hover:scale-105 transition-transform">
                                            {item.title}
                                        </h3>
                                        <p className="text-slate-400 leading-relaxed">
                                            {item.description}
                                        </p>
                                    </CardContent>
                                </Card>
                            </div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};

/**
 * Steps section.
 */
const StepsSection: React.FC<{ section: LandingSection }> = ({ section }) => {
    const items = section.items as LandingStepItem[];

    return (
        <section className="py-20 bg-slate-900">
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Section header */}
                <div className="text-center mb-16">
                    <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
                        {section.title}
                    </h2>
                    <p className="text-lg text-slate-400 max-w-2xl mx-auto">
                        {section.subtitle}
                    </p>
                </div>

                {/* Steps */}
                <div className="relative">
                    {/* Connection line */}
                    <div className="hidden md:block absolute top-1/2 left-0 right-0 h-0.5 bg-gradient-to-r from-violet-500/0 via-violet-500/50 to-violet-500/0" />

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {items.map((item, index) => (
                            <div 
                                key={index} 
                                className="relative text-center"
                            >
                                {/* Step number */}
                                <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center text-2xl font-bold text-white shadow-lg shadow-violet-500/30 relative z-10 overflow-hidden hover:scale-110 hover:rotate-12 hover:shadow-[0_0_30px_rgba(139,92,246,0.5)] transition-all">
                                    {/* Shimmer effect */}
                                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer-sweep" />
                                    <span className="relative z-10">{item.step}</span>
                                </div>

                                {/* Step content */}
                                <h3 className="text-xl font-semibold text-white mb-3 hover:scale-105 transition-transform">
                                    {item.title}
                                </h3>
                                <p className="text-slate-400 leading-relaxed">
                                    {item.description}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};

/**
 * Main sections renderer.
 */
const LandingSections: React.FC<LandingSectionsProps> = ({ sections }) => {
    return (
        <>
            {sections.map((section) => {
                switch (section.type) {
                    case 'feature-grid':
                        return <FeatureGrid key={section.id} section={section} />;
                    case 'steps':
                        return <StepsSection key={section.id} section={section} />;
                    default:
                        return null;
                }
            })}
        </>
    );
};

export default LandingSections;
